import * as Cmp from "../comparison";
import { clone, set } from "../obj";

import {
    CustomFilterTransformer,
    DataExtractor,
    DataExtractorGroup,
} from "./extractor";
import { DataSource, ArrayDataIterator, forEach } from "./source";
const { undefinedLast } = Cmp;

/**
 * Sort direction.
 */
type SortDirection = "ASC" | "DESC";

/**
 * Specify how a data view should be sorted.
 *
 * @remarks
 *
 * It only contains the direction of the sorting and a reference to an
 * extractor. The compare function of the extractor is used for the actual
 * sorting.
 */
interface Sorter {
    extractorId: string;
    direction: SortDirection;
}

/**
 * A data view associates a data source with a list of extractors.
 *
 * @remarks
 *
 * A data view contains everything to use the data source:
 * - the data source itself
 * - a list of extractor groups (makes it possible to extract various fields
 * from the data source items)
 * - a list of sorters (specifies how the data source is sorted)
 */
interface DataView<ITEM> {
    readonly dataSource: DataSource<ITEM>;
    readonly extractorGroups: Array<DataExtractorGroup<ITEM>>;
    readonly sorters: Array<Sorter>;
    readonly defaultCompareItems: Cmp.ComparisonFunction<ITEM>;
}

/**
 * Returns the extractors of all the groups of the data view.
 */
function getAllExtractors<ITEM>(
    dataView: DataView<ITEM>
): Array<DataExtractor<ITEM, any>> {
    return dataView.extractorGroups.map((group) => group.extractors).flat();
}

/**
 * Returns all extractors of the data view with the specified ids.
 */
function getExtractors<ITEM>(
    dataView: DataView<ITEM>,
    extractorIds: Array<string>
): Array<DataExtractor<ITEM, any>> {
    if (extractorIds.length === 0) {
        return [];
    }

    const extractorIdToIndex: { [extractorId: string]: number } = {};
    extractorIds.forEach((extractorId, index) => {
        if (extractorIdToIndex[extractorId] !== undefined) {
            throw new Error("Duplicate extractor " + extractorId);
        }
        extractorIdToIndex[extractorId] = index;
    });

    const extractors: Array<DataExtractor<ITEM, any>> = [];
    dataView.extractorGroups.forEach((group) => {
        group.extractors.forEach((extractor) => {
            const extractorIndex = extractorIdToIndex[extractor.id];
            if (extractorIndex !== undefined) {
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete extractorIdToIndex[extractor.id];
                extractors[extractorIndex] = extractor;
            }
        });
    });
    if (extractors.length !== extractorIds.length) {
        throw new Error(
            `Missing extractors: ${Object.keys(extractorIdToIndex).join(", ")}`
        );
    }
    return extractors;
}

/**
 * Returns a single extractor from a given data view.
 */
function getExtractor<ITEM>(dataView: DataView<ITEM>, extractorId: string) {
    return getExtractors(dataView, [extractorId])[0];
}

/**
 * Apply a callback to each value returned by a given extractor from the data source.
 */
function forEachValue<ITEM, VAL>(
    dataView: DataView<ITEM>,
    extractorId: string,
    callback: (value: VAL | undefined) => void
) {
    const extractor = getExtractor(dataView, extractorId) as DataExtractor<
        ITEM,
        VAL
    >;
    forEach(dataView.dataSource.iter(), (item) => {
        const val = extractor.getValue(item);
        callback(val);
    });
}

/**
 * An expanded item holding complex data.
 */
interface Expando<
    ITEM,
    ADDITIONAL = {
        [replacementExtractorId: string]: any;
    },
> {
    expandoId: string;
    baseItem: ITEM;
    additionalData: ADDITIONAL;
}

interface PartialData<T> {
    partialId: string;
    expandedValue: T;
}

/*
 * Specifies how to expand an item holding nested data.
 */
interface ExtractorExpander<VAL, EXP, FV = EXP> {
    extractorId: string;
    expand: (extractorValue: VAL) => Array<PartialData<EXP>>;
    compare: (item1Val: EXP, item2Val: EXP) => number;
    asString: (itemVal: EXP) => string;
    customFilterTransformer?: CustomFilterTransformer<EXP, FV>;
}

class Expander<ITEM> {
    private readonly expandItem: (item: ITEM) => Array<Expando<ITEM>>;
    private readonly replacementExtractors: Array<
        DataExtractor<Expando<ITEM>, any>
    >;

    constructor(
        expandItem: (item: ITEM) => Array<Expando<ITEM>>,
        replacementExtractors: Array<DataExtractor<Expando<ITEM>, any>>
    ) {
        this.expandItem = expandItem;
        this.replacementExtractors = replacementExtractors;
    }

    expand(dataView: DataView<ITEM>): DataView<Expando<ITEM>> {
        const baseIter = dataView.dataSource.iter();
        const expandedItems: Array<Expando<ITEM>> = [];
        while (true) {
            const baseItem = baseIter.next();
            if (baseItem === undefined) {
                break;
            }
            this.expandItem(baseItem).forEach((expandedItem) => {
                expandedItems.push(expandedItem);
            });
        }
        const mappedDataSource: DataSource<Expando<ITEM>> = {
            iter: () => {
                return new ArrayDataIterator(expandedItems, []);
            },
            getId: (item: Expando<ITEM>) => item.expandoId,
            clone: () => {
                return clone(mappedDataSource);
            },
            filtered(
                _predicate: (item: Expando<ITEM>) => boolean
            ): DataSource<Expando<ITEM>> {
                throw new Error("unimplemented");
            },
            filteredByIds(
                _excludedIds: Set<string>,
                _includedIds: Set<string>
            ): DataSource<Expando<ITEM>> {
                throw new Error("unimplemented");
            },
        };
        let replacedCount = 0;
        const mappedExtractorGroups = dataView.extractorGroups.map((group) => {
            const mappedExtractors = group.extractors.map((ext) => {
                const replacement = this.replacementExtractors.find(
                    (addExt) => addExt.id === ext.id
                );
                if (replacement) {
                    replacedCount++;
                    return replacement;
                } else {
                    return extractorToExpandoExtractor(ext);
                }
            });
            return {
                id: group.id,
                label: group.label,
                extractors: mappedExtractors,
            };
        });
        if (replacedCount < this.replacementExtractors.length) {
            throw new Error(
                "Some of the replacement extractors do not match the extractors in the original view"
            );
        }
        return {
            dataSource: mappedDataSource,
            extractorGroups: mappedExtractorGroups,
            sorters: dataView.sorters.slice(),
            defaultCompareItems: Cmp.map(
                (expando) => expando.baseItem,
                dataView.defaultCompareItems
            ),
        };
    }
}

function extractorToExpandoExtractor<ITEM, VAL, FV>(
    extractor: DataExtractor<ITEM, VAL, FV>
): DataExtractor<Expando<ITEM>, VAL, FV> {
    const expandoExtractor: DataExtractor<Expando<ITEM>, VAL, FV> = {
        id: extractor.id,
        label: extractor.label,
        getValue: (item: Expando<ITEM>) => extractor.getValue(item.baseItem),
        compare: (item1: Expando<ITEM>, item2: Expando<ITEM>) =>
            extractor.compare(item1.baseItem, item2.baseItem),
        asString: (item: Expando<ITEM>) => extractor.asString(item.baseItem),
    };
    if (extractor.customFilterTransformer) {
        const transformer: CustomFilterTransformer<ITEM, FV> =
            extractor.customFilterTransformer;
        expandoExtractor.customFilterTransformer = {
            getValue: (item: Expando<ITEM>): FV =>
                transformer.getValue(item.baseItem),
            asString: (value: FV): string => transformer.asString(value),
        };
    }
    return expandoExtractor;
}

/**
 * Specifies how the data returned by an extractor is expanded.
 *
 * Example:
 *
 * ```
 *  interface Employee {
 *      id: string,
 *      name: string,
 *  }
 *
 *  interface Department {
 *      id: string,
 *      name: string,
 *      tags: Array<string>,
 *      employees: Array<Employee>,
 *  }
 *
 *  const extractors: Array<DataExtractor<Department, any>> = [
 *      {
 *          id: "name",
 *          label: "Department",
 *          getValue: department => department.name,
 *          compare: (dept1, dept2) => dept1.name.localeCompare(dept2.name),
 *          asString: dept => dept.name,
 *      },
 *      {
 *          id: "employees",
 *          label: "Employees",
 *          getValue: department => department.employees,
 *          compare: (dept1, dept2) =>
 *             arrayCompare(dept1.employees, dept2.employees,
 *                  (emp1, emp2) => emp1.name.localeCompare(emp2.name)),
 *          asString: dept => dept.employees.map(emp => emp.name).join(", "),
 *      },
 *      {
 *          id: "tags",
 *          label: "Tags",
 *          getValue: department => department.tags,
 *          compare: (dept1, dept2) =>
 *              arrayCompare(dept1.tags, dept2.tags,
 *                 (tag1, tag2) => tag1.localeCompare(tag2)),
 *          asString: dept => dept.tags.join(", "),
 *      }
 *  ]
 *
 *  const expanders = [
 *      {
 *          extractorId: "tags",
 *          expand: tags => tags.map(tag => {
 *              return {
 *                  partialId: tag,
 *                  expandedValue: tag,
 *              }
 *          }),
 *          compare: (tag1, tag2) => tag1.localeCompare(tag2),
 *          asString: tag => tag,
 *      },
 *      {
 *          extractorId: "employees",
 *          expand: employees => employees.map(emp => {
 *              return {
 *                  partialId: emp.id,
 *                  expandedValue: emp,
 *              };
 *          }),
 *          compare: (emp1, emp2) => emp1.name.localeCompare(emp2.name),
 *          asString: emp => emp.name,
 *      },
 *  ]
 *
 * const departments = [
 *     {
 *         id: "dep-lg",
 *         name: "Logistics",
 *         employees: [
 *             {id: "bob", name: "Bob"},
 *             {id: "jane", name: "Jane"},
 *         ],
 *         tags: ["support", "cost center"],
 *     },
 *     {
 *         id: "dep-it",
 *         name: "IT",
 *         employees: [
 *             {id: "john", name: "John"},
 *         ],
 *         tags: [],
 *     },
 * ];
 * const view = expanded2(
 *     {
 *         dataSource: new ArrayDataSource(departments, dept => dept.id),
 *         extractors,
 *         sorters: []
 *     },
 *     expanders
 * );
 *
 * const expandedEmployees = toArray(view.dataSource.iter());
 * expandedEmployees == [
 *   {
 *     expandoId: "dep-lg-support-bob",
 *     baseItem: {
 *       id: "dep-lg",
 *       name: "Logistics",
 *       employees: [
 *         {id: "bob", name: "Bob"},
 *         {id: "jane", name: "Jane"}
 *       ],
 *       tags: ["support", "cost center"]
 *     },
 *     additionalData: {
 *       tags: "support",
 *       employees: {id: "bob", name: "Bob"}
 *     }
 *   },
 *   {
 *     expandoId: "dep-lg-cost center-bob",
 *     baseItem: {
 *       id: "dep-lg",
 *       name: "Logistics",
 *       employees: [
 *         {id: "bob", name: "Bob"},
 *         {id: "jane", name: "Jane"}
 *       ],
 *       tags: ["support", "cost center"]
 *     },
 *     additionalData: {
 *       tags: "cost center",
 *       employees: {id: "bob", name: "Bob"}
 *     }
 *   },
 *   {
 *     expandoId: "dep-lg-support-jane",
 *     baseItem: {
 *       id: "dep-lg",
 *       name: "Logistics",
 *       employees: [
 *         {id: "bob", name: "Bob"},
 *         {id: "jane", name: "Jane"}
 *       ],
 *       tags: ["support", "cost center"]
 *     },
 *     additionalData: {
 *       tags: "support",
 *       employees: {"id": "jane", "name": "Jane"}
 *     }
 *   },
 *   {
 *     expandoId: "dep-lg-cost center-jane",
 *     baseItem: {
 *       id: "dep-lg",
 *       name: "Logistics",
 *       employees: [
 *         {id: "bob", name: "Bob"},
 *         {id: "jane", name: "Jane"}
 *       ],
 *       tags: ["support", "cost center"]
 *     },
 *     additionalData: {
 *       tags: "cost center",
 *       employees: {id: "jane", name: "Jane"}
 *     }
 *   },
 *   {
 *     expandoId: "dep-it-john",
 *     baseItem: {
 *       id: "dep-it",
 *       name: "IT",
 *       employees: [
 *         {id: "john", name: "John"}
 *       ],
 *       tags: []
 *     },
 *     additionalData: {
 *       employees: {id: "john", name: "John"}
 *     }
 *   }
 * ];
 * ```
 */

function expanded<ITEM>(
    dataView: DataView<ITEM>,
    extractorExpanders: Array<ExtractorExpander<any, any>>
): DataView<Expando<ITEM>> {
    const expandItem = (baseItem: ITEM) => {
        let expandos: Array<Expando<ITEM>> = [
            {
                baseItem,
                expandoId: dataView.dataSource.getId(baseItem),
                additionalData: {},
            },
        ];
        extractorExpanders.forEach((expander) => {
            const baseExtractor = getExtractor(dataView, expander.extractorId);
            if (!baseExtractor) {
                throw new Error(
                    "Can't find base extractor for expander " +
                        expander.extractorId
                );
            }
            const newExpandos: Array<Expando<ITEM>> = [];
            const baseValue = baseExtractor.getValue(baseItem);
            expander.expand(baseValue).forEach((partialIdAndValue) => {
                const { partialId, expandedValue } = partialIdAndValue;
                expandos.forEach((expando) => {
                    newExpandos.push(
                        set(expando, {
                            expandoId: `${expando.expandoId}-${partialId}`,
                            additionalData: set(expando.additionalData, {
                                [expander.extractorId]: expandedValue,
                            }),
                        })
                    );
                });
            });
            if (newExpandos.length > 0) {
                expandos = newExpandos;
            }
            // Otherwise, the expander did not find anything to expand, keep the older values
            // They simply won't have a key in additionalData matching this extractor
        });
        return expandos;
    };
    const replacementExtractors = extractorExpanders.map((expander) => {
        const baseExtractor = getExtractor(dataView, expander.extractorId);
        if (!baseExtractor) {
            throw new Error(
                "Can't find base extractor for expander " + expander.extractorId
            );
        }
        const getValue = (item: Expando<ITEM>) => {
            return item.additionalData[expander.extractorId];
        };
        const expanderDataComparator = undefinedLast(expander.compare);
        const extractor: DataExtractor<Expando<ITEM>, any> = {
            id: expander.extractorId,
            label: baseExtractor.label,
            getValue,
            compare: (item1, item2) => {
                return expanderDataComparator(getValue(item1), getValue(item2));
            },
            asString: (item) => expander.asString(getValue(item)),
        };
        return extractor;
    });
    return new Expander(expandItem, replacementExtractors).expand(dataView);
}
export type {
    DataView,
    Sorter,
    SortDirection,
    ExtractorExpander,
    Expander,
    Expando,
    PartialData,
};
export {
    expanded,
    getExtractors,
    getExtractor,
    getAllExtractors,
    forEachValue,
};
